import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ContextItem,
  EditablePair,
} from "../api/responsePayload/KnowledgeResponse";
import { GetContext } from "../api/ContextApiCall";
import { showConsoleError } from "../util/ConsoleMessage";
import { setPagination } from "./pagination.slice";

interface onContextState {
  isContextSelected: boolean;
  callApproveApi: boolean;
  items: ContextItem[];
  addedPairs: { [key: number]: EditablePair[] };
  loading: boolean;
  error?: string;
}

const initialState: onContextState = {
  isContextSelected: false,
  callApproveApi: false,
  items: [],
  addedPairs: {},
  loading: false,
};

export const fetchContext = createAsyncThunk(
  "context/fetch",
  async (
    args: { endpoint?: string; page?: number } | undefined,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const res = await GetContext(args?.endpoint, args?.page);
      if (res) {
        dispatch(
          setPagination({
            currentPage: res.current_page,
            nextPageUrl: res.next,
            prevPageUrl: res.previous,
            totalPages: res.total_pages,
            totalCount: res.count,
          })
        );
        return res.results as ContextItem[];
      }
      return [] as ContextItem[];
    } catch (e: any) {
      showConsoleError("fetchContext error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const contextSlice = createSlice({
  name: "context",
  initialState: initialState,
  reducers: {
    setContextSelected(state, action: PayloadAction<{ isSelected: boolean }>) {
      state.isContextSelected = action.payload.isSelected;
    },

    setCallApproveApi(state, action: PayloadAction<{ isSelected: boolean }>) {
      state.callApproveApi = action.payload.isSelected;
    },

    setContextItems(state, action: PayloadAction<ContextItem[]>) {
      state.items = action.payload;
    },

    removeContextItem(state, action: PayloadAction<number>) {
      state.items = state.items.filter((i) => i.id !== action.payload);
    },

    clearContext(state) {
      state.items = [];
    },

    addEditablePair(
      state,
      action: PayloadAction<{ contextId: number; pairs: EditablePair[] }>
    ) {
      const { contextId, pairs } = action.payload;
      if (!state.addedPairs[contextId]) {
        state.addedPairs[contextId] = [];
      }
      state.addedPairs[contextId].push(...pairs);
    },

    deleteEditablePair(state, action: PayloadAction<{ contextId: number }>) {
      const { contextId } = action.payload;
      if (state.addedPairs[contextId]) {
        delete state.addedPairs[contextId];
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchContext.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchContext.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchContext.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch context";
      });
  },
});

export const {
  setContextSelected,
  setCallApproveApi,
  addEditablePair,
  deleteEditablePair,
} = contextSlice.actions;

export default contextSlice.reducer;
